import React, { useState } from "react";
import { LaunchHeroStep } from "./LaunchHeroStep";
import { CodeAnalysisStep } from "./CodeAnalysisStep";
import { BIMViewerStep } from "./BIMViewerStep";
import { OnboardingProgress } from "./OnboardingProgress";
import { AuthHeader } from "../auth/AuthHeader";

export type OnboardingStep = "launch-hero" | "code-analysis" | "bim-viewer";

interface OnboardingFlowProps {
  onComplete?: () => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("launch-hero");
  const [completedSteps, setCompletedSteps] = useState<OnboardingStep[]>([]);

  const steps: { id: OnboardingStep; title: string; description: string }[] = [
    {
      id: "launch-hero",
      title: "Launch Hero",
      description: "Meet your AI compliance assistant",
    },
    {
      id: "code-analysis",
      title: "Code Analysis",
      description: "Extract regulations from PDFs",
    },
    {
      id: "bim-viewer",
      title: "BIM Viewer",
      description: "Analyze your 3D models",
    },
  ];

  const handleStepComplete = (stepId: OnboardingStep) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const handleNextStep = () => {
    const currentIndex = steps.findIndex((step) => step.id === currentStep);
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      setCurrentStep(nextStep.id);
    } else {
      // All steps completed
      onComplete?.();
    }
  };

  const handlePreviousStep = () => {
    const currentIndex = steps.findIndex((step) => step.id === currentStep);
    if (currentIndex > 0) {
      const previousStep = steps[currentIndex - 1];
      setCurrentStep(previousStep.id);
    }
  };

  const canProceed = completedSteps.includes(currentStep);

  return (
    <div className="min-h-screen bg-custom-light-50">
      {/* Header with Auth */}
      <header className="bg-custom-navy border-b border-custom-dark px-4 sm:px-6 lg:px-8 shadow-sm">
        <div className="max-w-full mx-auto flex justify-between items-center h-16">
          <div className="flex items-center">
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">HeroCon.Ai</h1>
            </div>
          </div>
          <AuthHeader />
        </div>
      </header>

      {/* Progress Bar */}
      <OnboardingProgress
        steps={steps}
        currentStep={currentStep}
        completedSteps={completedSteps}
      />

      {/* Step Content - More Space for BIM Viewer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {currentStep === "launch-hero" && (
          <LaunchHeroStep
            onComplete={() => handleStepComplete("launch-hero")}
            onNext={handleNextStep}
            canProceed={canProceed}
          />
        )}

        {currentStep === "code-analysis" && (
          <CodeAnalysisStep
            onComplete={() => handleStepComplete("code-analysis")}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
            canProceed={canProceed}
          />
        )}

        {currentStep === "bim-viewer" && (
          <BIMViewerStep
            onComplete={() => handleStepComplete("bim-viewer")}
            onPrevious={handlePreviousStep}
            canProceed={canProceed}
          />
        )}
      </div>
    </div>
  );
};
